import { shortSpinId } from '../../../Model/Spin/spinId.js'
import React, { useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import type { SpinMembershipFlow } from '../../../Generated/SpinMembershipFlow.ts'
import { Card, KpiTile } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { InfoTip } from './SpinUi.js'
import { sortByNumber, type SortDir, tsLabel } from './spinMetrics.logic.js'
import { useSelectedRun } from './useSelectedRun.js'
import { useSpinMembershipFlow } from './useSpinData.js'

/** The four membership-flow segments in display order, with colors. */
const FLOW_SEGMENTS: Array<{ key: 'stayed' | 'leftBehind' | 'addedFromOther' | 'addedNew'; label: string; color: string }> = [
  { key: 'stayed', label: 'Stayed', color: '#22c55e' },
  { key: 'leftBehind', label: 'Left', color: '#ef4444' },
  { key: 'addedFromOther', label: 'Added (from other SPIN)', color: '#f59e0b' },
  { key: 'addedNew', label: 'Added (new)', color: '#3b82f6' }
]

/** churn = left + all arrivals (from another SPIN or brand-new). Matches the backend's per-SPIN sort key. */
const churnOf = (s: SpinMembershipFlow): number => s.leftBehind + s.addedFromOther + s.addedNew

/** Sortable numeric columns of the per-SPIN table. */
type SortKey = 'members' | 'stayed' | 'left' | 'addedOther' | 'addedNew' | 'churn' | 'retention'
const COLUMNS: Array<{ key: SortKey; label: string; value: (s: SpinMembershipFlow) => number }> = [
  { key: 'members', label: 'Members', value: (s) => s.memberCount },
  { key: 'stayed', label: 'Stayed', value: (s) => s.stayed },
  { key: 'left', label: 'Left', value: (s) => s.leftBehind },
  { key: 'addedOther', label: 'Added (other)', value: (s) => s.addedFromOther },
  { key: 'addedNew', label: 'Added (new)', value: (s) => s.addedNew },
  { key: 'churn', label: 'Churn', value: churnOf },
  { key: 'retention', label: 'Retention', value: (s) => s.retention ?? -1 }
]

const CompositionBar: React.FC<{ flow: Record<'stayed' | 'leftBehind' | 'addedFromOther' | 'addedNew', number> }> = ({ flow }) => {
  const total = FLOW_SEGMENTS.reduce((sum, s) => sum + flow[s.key], 0)
  if (total === 0) return <p className="text-gray-500 text-sm">No membership events.</p>
  return (
    <div>
      <div className="flex h-6 w-full overflow-hidden rounded">
        {FLOW_SEGMENTS.map((s) => {
          const pct = (flow[s.key] / total) * 100
          if (pct === 0) return null
          return (
            <div key={s.key} style={{ width: `${pct}%`, backgroundColor: s.color }} title={`${s.label}: ${flow[s.key].toLocaleString()}`} />
          )
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 mt-2">
        {FLOW_SEGMENTS.map((s) => (
          <span key={s.key} className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: s.color }} />
            {s.label}: {flow[s.key].toLocaleString()}
          </span>
        ))}
      </div>
    </div>
  )
}

/** Flow-type explanations, shown as `InfoTip` tooltips on the KPI tiles (aligned to FLOW_CTE). */
const FLOW_TIPS: Record<'carriedForward' | 'stayed' | 'leftBehind' | 'addedFromOther' | 'addedNew', string> = {
  carriedForward:
    'OTUs still on the same SPIN as the prior run — their cluster was not reminted (kept its id, no new membership row). Usually the vast majority; the four counts to the right cover only the SPINs this run freshly minted.',
  stayed: 'Present in a predecessor SPIN and still a member after the remint.',
  leftBehind: 'Present in a predecessor SPIN but no longer a member of the reminted SPIN.',
  addedFromOther: 'New to this SPIN, but already existed in the prior run’s SPIN space — moved in from elsewhere.',
  addedNew: 'Brand-new OTU, absent from the prior run entirely.'
}

const CHURN_TIP = 'Left + Added (from other) + Added (new) — how much the SPIN’s membership moved. The default sort.'

export const SpinFlowPage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()
  const { selected } = sel
  const { flow, loading: flowLoading } = useSpinMembershipFlow(selected?.id)

  const [sortKey, setSortKey] = useState<SortKey>('churn')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const rows = flow?.spins ?? []
  const sortedRows = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sortKey)
    return col ? sortByNumber(rows, col.value, sortDir) : rows
  }, [rows, sortKey, sortDir])

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    else {
      setSortKey(key)
      setSortDir('desc')
    }
  }
  const sortIndicator = (key: SortKey) => (key === sortKey ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '')

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN · Membership flow" sel={sel} />

      {flowLoading && <p className="text-gray-500">Loading membership flow…</p>}
      {!flowLoading && selected && flow && (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">
            Membership flow — {selected ? tsLabel(selected.startedAt) : ''}
            <InfoTip tip="Following actual OTUs across this run vs its predecessor: who stayed, who left, who arrived (from another SPIN or brand-new). This is about which OTUs move; Drift is about the taxonomy label moving." />
          </h2>
          {!flow.hasPredecessor && <p className="text-gray-500 text-sm">First run — no predecessor; all members counted as new.</p>}

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KpiTile
              label={
                <>
                  Carried forward
                  <InfoTip tip={FLOW_TIPS.carriedForward} />
                </>
              }
              value={flow.total.carriedForward.toLocaleString()}
            />
            <KpiTile
              label={
                <>
                  Stayed (reminted)
                  <InfoTip tip={FLOW_TIPS.stayed} />
                </>
              }
              value={flow.total.stayed.toLocaleString()}
            />
            <KpiTile
              label={
                <>
                  Left
                  <InfoTip tip={FLOW_TIPS.leftBehind} />
                </>
              }
              value={flow.total.leftBehind.toLocaleString()}
            />
            <KpiTile
              label={
                <>
                  Added (from other SPIN)
                  <InfoTip tip={FLOW_TIPS.addedFromOther} />
                </>
              }
              value={flow.total.addedFromOther.toLocaleString()}
            />
            <KpiTile
              label={
                <>
                  Added (new)
                  <InfoTip tip={FLOW_TIPS.addedNew} />
                </>
              }
              value={flow.total.addedNew.toLocaleString()}
            />
          </div>
          <Card title="Membership composition (freshly-minted SPINs)">
            <CompositionBar flow={flow.total} />
          </Card>
          <Card title={`Per-SPIN membership flow — full list (${rows.length.toLocaleString()})`}>
            <p className="text-xs text-gray-500 mb-2">Every SPIN this run minted. Click a column header to sort; default is churn, highest first.</p>
            <div className="overflow-x-auto max-h-[32rem]">
              <table className="table table-xs table-pin-rows">
                <thead>
                  <tr>
                    <th>SPIN</th>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        className="text-right cursor-pointer select-none whitespace-nowrap"
                        onClick={() => toggleSort(c.key)}
                      >
                        {c.label}
                        {c.key === 'churn' && <InfoTip tip={CHURN_TIP} />}
                        {sortIndicator(c.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((s) => (
                    <tr key={s.spinId}>
                      <td className="font-mono">
                        <Link to={`/admin/spin/s/${s.spinId}`} className="link link-primary">
                          {shortSpinId(s.spinId)}
                        </Link>
                      </td>
                      <td className="text-right">{s.memberCount.toLocaleString()}</td>
                      <td className="text-right">{s.stayed.toLocaleString()}</td>
                      <td className="text-right">{s.leftBehind.toLocaleString()}</td>
                      <td className="text-right">{s.addedFromOther.toLocaleString()}</td>
                      <td className="text-right">{s.addedNew.toLocaleString()}</td>
                      <td className="text-right">{churnOf(s).toLocaleString()}</td>
                      <td className="text-right">{s.retention == null ? '—' : s.retention.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      )}
    </div>
  )
}
